/**
 * Hooks pour gérer les connexions email
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  EmailConnection,
  EmailConnectionCreate,
  EmailConnectionUpdate,
  EmailPattern,
  EmailPatternCreate,
  EmailPatternUpdate,
  EmailMessage,
  EmailConnectionStats,
  OAuthAuthorizeResponse,
  EmailMessageConfirmRequest,
} from '../types/email';

const API_BASE = '/email';

// === Connection Hooks ===

export function useEmailConnections() {
  return useQuery({
    queryKey: ['email-connections'],
    queryFn: async () => {
      const { data } = await api.get<EmailConnection[]>(`${API_BASE}/connections`);
      return data;
    },
  });
}

export function useEmailConnection(connectionId: number) {
  return useQuery({
    queryKey: ['email-connection', connectionId],
    queryFn: async () => {
      const { data } = await api.get<EmailConnection>(`${API_BASE}/connections/${connectionId}`);
      return data;
    },
    enabled: !!connectionId,
  });
}

export function useCreateEmailConnection() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (connection: EmailConnectionCreate) => {
      const { data } = await api.post<EmailConnection>(`${API_BASE}/connections`, connection);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-connections'] });
    },
  });
}

export function useUpdateEmailConnection() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...update }: EmailConnectionUpdate & { id: number }) => {
      const { data } = await api.patch<EmailConnection>(`${API_BASE}/connections/${id}`, update);
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['email-connections'] });
      queryClient.invalidateQueries({ queryKey: ['email-connection', variables.id] });
    },
  });
}

export function useDeleteEmailConnection() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (connectionId: number) => {
      await api.delete(`${API_BASE}/connections/${connectionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-connections'] });
    },
  });
}

// === OAuth Hooks ===

export function useGmailAuthorize() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.get<OAuthAuthorizeResponse>(`${API_BASE}/oauth/gmail/authorize`);
      return data;
    },
  });
}

export function useOutlookAuthorize() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.get<OAuthAuthorizeResponse>(`${API_BASE}/oauth/outlook/authorize`);
      return data;
    },
  });
}

// === Pattern Hooks ===

export function useEmailPatterns(connectionId: number) {
  return useQuery({
    queryKey: ['email-patterns', connectionId],
    queryFn: async () => {
      const { data } = await api.get<EmailPattern[]>(`${API_BASE}/connections/${connectionId}/patterns`);
      return data;
    },
    enabled: !!connectionId,
  });
}

export function useCreateEmailPattern() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ connectionId, ...pattern }: EmailPatternCreate & { connectionId: number }) => {
      const { data } = await api.post<EmailPattern>(
        `${API_BASE}/connections/${connectionId}/patterns`,
        pattern
      );
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['email-patterns', variables.connectionId] });
    },
  });
}

export function useUpdateEmailPattern() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ patternId, connectionId, ...update }: EmailPatternUpdate & { patternId: number; connectionId: number }) => {
      const { data } = await api.patch<EmailPattern>(`${API_BASE}/patterns/${patternId}`, update);
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['email-patterns', variables.connectionId] });
    },
  });
}

export function useDeleteEmailPattern() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ patternId, connectionId }: { patternId: number; connectionId: number }) => {
      await api.delete(`${API_BASE}/patterns/${patternId}`);
      return connectionId;
    },
    onSuccess: (connectionId) => {
      queryClient.invalidateQueries({ queryKey: ['email-patterns', connectionId] });
    },
  });
}

// === Message Hooks ===

export function useEmailMessages(connectionId: number, status?: string) {
  return useQuery({
    queryKey: ['email-messages', connectionId, status],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      const { data } = await api.get<EmailMessage[]>(
        `${API_BASE}/connections/${connectionId}/messages?${params}`
      );
      return data;
    },
    enabled: !!connectionId,
  });
}

export function usePendingMessages(connectionId: number) {
  return useQuery({
    queryKey: ['email-pending', connectionId],
    queryFn: async () => {
      const { data } = await api.get<EmailMessage[]>(
        `${API_BASE}/connections/${connectionId}/pending`
      );
      return data;
    },
    enabled: !!connectionId,
    refetchInterval: 30000, // Refresh every 30s
  });
}

export function useConfirmMessage() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ messageId, connectionId, ...request }: EmailMessageConfirmRequest & { messageId: number; connectionId: number }) => {
      const { data } = await api.post(`${API_BASE}/messages/${messageId}/confirm`, request);
      return { ...data, connectionId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['email-pending', result.connectionId] });
      queryClient.invalidateQueries({ queryKey: ['email-messages', result.connectionId] });
    },
  });
}

// === Stats Hooks ===

export function useEmailStats(connectionId: number) {
  return useQuery({
    queryKey: ['email-stats', connectionId],
    queryFn: async () => {
      const { data } = await api.get<EmailConnectionStats>(
        `${API_BASE}/connections/${connectionId}/stats`
      );
      return data;
    },
    enabled: !!connectionId,
    refetchInterval: 60000, // Refresh every minute
  });
}

// === Fetch Emails Hook ===

export function useFetchEmails() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (connectionId: number) => {
      console.log('[useFetchEmails] Calling POST', `${API_BASE}/connections/${connectionId}/fetch`);
      const { data } = await api.post<{ success: boolean; emails_fetched: number; message: string }>(
        `${API_BASE}/connections/${connectionId}/fetch`
      );
      console.log('[useFetchEmails] Response:', data);
      return { ...data, connectionId };
    },
    onError: (error) => {
      console.error('[useFetchEmails] Error:', error);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['email-pending', result.connectionId] });
      queryClient.invalidateQueries({ queryKey: ['email-messages', result.connectionId] });
      queryClient.invalidateQueries({ queryKey: ['email-stats', result.connectionId] });
    },
  });
}
